/**
 * @fileoverview Util functions used by ListingsLaunchTaskFlow
 *
 * @author Kuber Sethi (ksethi@yext.com)
 */

import {EntityInfo} from '/src/com/yext/listingslaunchstorm/js/data/entityinfo';
import {GetVerificationMethodsResponse} from '/src/com/yext/listingslaunchstorm/js/data/getverificationmethodsresponse';
import {ListingsLaunchStep, ListingsLaunchStepByValue} from '/src/com/yext/listingslaunchstorm/js/data/listingslaunchstep';
import {MatchedListing} from '/src/com/yext/listingslaunchstorm/js/data/matchedlisting';
import {VerificationMethod} from '/src/com/yext/listingslaunchstorm/js/data/verificationmethod';
import {VerifiedListing} from '/src/com/yext/listingslaunchstorm/js/data/verifiedlisting';

/**
 * Creates a key for use in React Nodes and component IDs.
 * @param {number} listingsLaunchStep
 * @param {number} id
 * @return {string}
 */
export const createListingsLaunchStepKey = (listingsLaunchStep, id) => {
  return ListingsLaunchStepByValue[listingsLaunchStep] + '-' + id;
};

/**
 * Creates a mapping of verification method to a users verification destination for that method,
 * if present, from a given GetVerificationMethodsResponse
 * @param {GetVerificationMethodsResponse} getVerificationMethodsResponse
 * @return {Object<VerificationMethod, string>}
 */
export const createVerificationMethodToDestinationMap = getVerificationMethodsResponse => {
  const verificationDestinationMap = {};

  getVerificationMethodsResponse?.methods.map(verificationMethodString => {
    const verificationMethod = VerificationMethod[verificationMethodString];
    switch (verificationMethod) {
      case VerificationMethod.SMS:
        if (getVerificationMethodsResponse.smsNumber) {
          verificationDestinationMap[VerificationMethod.SMS] = getVerificationMethodsResponse.smsNumber;
        }
      case VerificationMethod.EMAIL:
        if (getVerificationMethodsResponse.emailUser && getVerificationMethodsResponse.emailDomain) {
          verificationDestinationMap[VerificationMethod.EMAIL] = getVerificationMethodsResponse.emailUser + '@' + getVerificationMethodsResponse.emailDomain;
        }
      case VerificationMethod.PHONE_CALL:
        if (getVerificationMethodsResponse.phoneNumber) {
          verificationDestinationMap[VerificationMethod.PHONE_CALL] = getVerificationMethodsResponse.phoneNumber;
        }
      case VerificationMethod.POSTCARD:
        if (getVerificationMethodsResponse.addressTokens) {
          verificationDestinationMap[VerificationMethod.POSTCARD] = getVerificationMethodsResponse.addressTokens.join(', ');
        }
    }
  });

  return verificationDestinationMap;
};

/**
 * Retrieve a single match from list of all selected matches, using the specified entity's id.
 * @param {!EntityInfo} linkedEntity
 * @param {!Array<!MatchedListing>} matchedListings
 * @return {!MatchedListing | undefined}
 */
export function getMatchForEntity(linkedEntity, matchedListings) {
  return matchedListings.find(matchedListing => matchedListing.entityId === linkedEntity.id);
}

/**
 * Retrieve a single verification from list of all verified, using the specified entity's id.
 * @param {!EntityInfo} linkedEntity
 * @param {!Array<!VerifiedListing>} verifiedListings
 * @return {!VerifiedListing | undefined}
 */
export function getVerificationForEntity(linkedEntity, verifiedListings) {
  return verifiedListings.find(verifiedListing => verifiedListing.entityId === linkedEntity.id);
}

/**
 * Get the launch flow step that we should start on, based on the initial data received from the
 * backend when loading the page.
 * @param {? | null} linkedAccount
 * @param {!Array<!EntityInfo>} linkedEntities
 * @param {!Array<!MatchedListing>} matchedListings
 * @param {!Array<!VerifiedListing>} verifiedListings
 * @return {number}
 */
export function getInitialStep(linkedAccount, linkedEntities, matchedListings, verifiedListings) {
  const businessId = Number(window['businessId']);
  if (businessId === 427914) { // Start the flow at the first step for Luxottica
    return ListingsLaunchStep.PUBLISHER_SETUP;
  }
  if (linkedAccount == null) {
    return ListingsLaunchStep.PUBLISHER_SETUP;
  }
  if (matchedListings.length !== linkedEntities.length) {
    return ListingsLaunchStep.MATCH_ENTITY;
  }
  if (verifiedListings.length !== linkedEntities.length) {
    return ListingsLaunchStep.VERIFICATION;
  }

  // If the flow is complete (we've started with entities that are all already linked, matched,
  // and verified), so go back to the first match step in case the user wants to change the match.
  return ListingsLaunchStep.MATCH_ENTITY;
}
